import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fileNameNormalizer } from '@app/shared/misc/fileNameNormalizer';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Document } from '../entity/document';
import {
  LundifyShareDocumentResponse,
  LundifyProviderLifecycleStatus,
  LundifyShareOpenedResponse,
  LundifyShareRecipient,
  LundifySharedDocumentPage,
} from '../entity/lundify-shared-document';
import { HttpAuthorizationHeader } from '../misc/httpauthorizationheader';
import { BLService } from './bl.service';

@Injectable()
export class DocumentService extends BLService {
  private options = new HttpAuthorizationHeader('application/json');
  private RESOURCE_ENDPOINT = 'document';
  private urlV2 = environment.serverUrlV2 + this.RESOURCE_ENDPOINT;

  public getFilesAndFolders(clientId: number, path: string): Observable<Document[]> {
    return this.http
      .post<Document[]>(this.getFetchUrl(clientId), { fileSpec: path }, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  public getFile(clientId: number, path: string): Observable<BinaryType> {
    const options = this.options.getAuthorizationHeaderWithResponseType('arraybuffer');
    options.headers.delete('Content-Type');
    return this.http
      .post<BinaryType>(this.getFetchUrl(clientId), { fileSpec: path }, options)
      .pipe(this.catchErrorAndShowMessage());
  }

  createDirectory(clientId: number, pathToAddFolder: string, newFolderName: string): Observable<null> {
    return this.http
      .post<null>(
        this.getAddFolderUrl(clientId),
        { fileSpec: `${pathToAddFolder}${fileNameNormalizer(newFolderName)}/` },
        this.options.getAuthorizationHeader(),
      )
      .pipe(this.catchErrorAndShowMessage());
  }

  saveFile(clientId: number, path: string, file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, fileNameNormalizer(file.name));
    formData.append('fileSpec', path);

    return this.http
      .post<any>(this.getUploadUrl(clientId), formData, this.options.getAuthorizationHeaderWithoutContentType())
      .pipe(this.catchErrorAndShowMessage());
  }

  deleteFileOrFolder(clientId: number, pathToDelete: string): Observable<null> {
    return this.http
      .post<null>(this.getRemoveUrl(clientId), { fileSpec: pathToDelete }, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  renameFileOrFolder(clientId: number, pathToRename: string, newName: string): Observable<null> {
    const body = { fileSpec: pathToRename, newName: fileNameNormalizer(newName) };
    return this.http
      .post<null>(this.getUpdateUrl(clientId), body, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  // Documents the client shared from Lundify (INBOUND), fetched live via BLA polling.
  // "data" on each item is already the base64-encoded file - no extra download call needed.
  public getIncomingSharedDocuments(
    clientId: number,
    providerLifecycleStatus?: LundifyProviderLifecycleStatus,
    page = 0,
    size = 50,
  ): Observable<LundifySharedDocumentPage> {
    return this.getSharedDocuments({ clientId, direction: 'incoming', providerLifecycleStatus, page, size });
  }

  // Documents Workflow shared to the client's Lundify company (OUTBOUND).
  public getOutgoingSharedDocuments(
    clientId: number,
    providerLifecycleStatus?: LundifyProviderLifecycleStatus,
    page = 0,
    size = 50,
  ): Observable<LundifySharedDocumentPage> {
    return this.getSharedDocuments({ clientId, direction: 'outgoing', providerLifecycleStatus, page, size });
  }

  // Archives Workflow's own copy of a shared document (either direction). Independent from the
  // Lundify user's archive state and irreversible - BLA offers no unarchive.
  public archiveSharedDocument(clientId: number, documentShareId: number): Observable<void> {
    return this.http
      .put<void>(`${this.urlV2}/${clientId}/shared/${documentShareId}/archive`, null, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  // Replaces the whole recipient list of an already uploaded document in one call - no file is re-sent.
  public replaceSharedDocumentRecipients(
    clientId: number,
    documentShareId: number,
    recipients: LundifyShareRecipient[],
  ): Observable<LundifyShareRecipient[]> {
    return this.http
      .put<LundifyShareRecipient[]>(
        `${this.urlV2}/${clientId}/shared/${documentShareId}/recipients`,
        { recipients },
        this.options.getAuthorizationHeader(),
      )
      .pipe(this.catchErrorAndShowMessage());
  }

  // Shares a local file with the client's Lundify company (Workflow -> Lundify, OUTBOUND).
  // Recipients are optional: without them the file is only uploaded (BLA keeps it as NOT_SHARED)
  // and recipients are added later by replacing the recipient list (replaceSharedDocumentRecipients),
  // without re-sending the file. The call is idempotent per file content on BLA's side
  // (docs/design/workflow-shared-documents-api.md, section 7).
  public shareDocumentToLundify(
    clientId: number,
    file: File,
    recipients: LundifyShareRecipient[],
    path?: string,
  ): Observable<LundifyShareDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file, fileNameNormalizer(file.name));
    formData.append('recipients', new Blob([JSON.stringify(recipients)], { type: 'application/json' }));
    if (path) {
      formData.append('path', new Blob([path], { type: 'text/plain' }));
    }
    return this.http
      .post<LundifyShareDocumentResponse>(
        `${this.urlV2}/${clientId}/shared/send`,
        formData,
        this.options.getAuthorizationHeaderWithoutContentType(),
      )
      .pipe(this.catchErrorAndShowMessage());
  }

  // Empty "Outgoing documents" folders stored in Workflow (BLA keeps folders only as a document's
  // "path"); folders that contain documents are derived from the documents themselves.
  public getSharedFolders(clientId: number): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.urlV2}/${clientId}/shared/folders`, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  public createSharedFolder(clientId: number, path: string): Observable<{ path: string }> {
    return this.http
      .post<{ path: string }>(`${this.urlV2}/${clientId}/shared/folders`, { path }, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  public deleteSharedFolder(clientId: number, path: string): Observable<void> {
    const params = new HttpParams().set('path', path);
    return this.http
      .delete<void>(`${this.urlV2}/${clientId}/shared/folders`, { ...this.options.getAuthorizationHeader(), params })
      .pipe(this.catchErrorAndShowMessage());
  }

  // Confirms an INBOUND shared document was opened so Lundify shows "Read" on its next poll
  // (docs/design/workflow-shared-documents-api.md, section 5). Idempotent - safe to call every
  // time the file is downloaded.
  public markIncomingDocumentOpened(clientId: number, documentShareId: number): Observable<LundifyShareOpenedResponse> {
    return this.http
      .put<LundifyShareOpenedResponse>(
        `${this.urlV2}/${clientId}/shared/incoming/${documentShareId}/opened`,
        null,
        this.options.getAuthorizationHeader(),
      )
      .pipe(this.catchErrorAndShowMessage());
  }

  // Fallback download for an OUTBOUND shared document when the polling item has no inline "data"
  // (docs/design/workflow-shared-documents-api.md, section 6).
  public downloadOutgoingDocumentFile(clientId: number, reference: string): Observable<ArrayBuffer> {
    const options = this.options.getAuthorizationHeaderWithResponseType('arraybuffer');
    const params = new HttpParams().set('reference', reference);
    return this.http
      .get<ArrayBuffer>(`${this.urlV2}/${clientId}/shared/outgoing/file`, { ...options, params })
      .pipe(this.catchErrorAndShowMessage());
  }

  private getSharedDocuments(request: {
    clientId: number;
    direction: 'incoming' | 'outgoing';
    providerLifecycleStatus?: LundifyProviderLifecycleStatus;
    page: number;
    size: number;
  }): Observable<LundifySharedDocumentPage> {
    const options = this.options.getAuthorizationHeader();
    let params = new HttpParams().set('page', request.page).set('size', request.size);
    if (request.providerLifecycleStatus) {
      params = params.set('providerLifecycleStatus', request.providerLifecycleStatus);
    }
    return this.http
      .get<LundifySharedDocumentPage>(`${this.urlV2}/${request.clientId}/shared/${request.direction}`, {
        ...options,
        params,
      })
      .pipe(this.catchErrorAndShowMessage());
  }

  private getFetchUrl(clientId: number): string {
    return this.getUrl(clientId, 'fetch');
  }

  private getUploadUrl(clientId: number): string {
    return this.getUrl(clientId, 'upload');
  }

  private getUpdateUrl(clientId: number): string {
    return this.getUrl(clientId, 'update');
  }

  private getRemoveUrl(clientId: number): string {
    return this.getUrl(clientId, 'remove');
  }

  private getAddFolderUrl(clientId: number): string {
    return this.getUrl(clientId, 'add-folder');
  }

  private getUrl(clientId: number, path: string): string {
    return `${this.urlV2}/${clientId}/${path}`;
  }
}
